import React from "react";
import { generatedBetaClosureStatus } from "../data/generatedBetaClosureStatus.js";
import { generatedEvidenceGapPriorityLedger } from "../data/generatedEvidenceGapPriorityLedger.js";
import { generatedReleaseEvidenceLedger } from "../data/generatedReleaseEvidenceLedger.js";
import { generatedScenarioDecisionLedger } from "../data/generatedScenarioDecisionLedger.js";
import { round } from "../science/kernel.mjs";
import {
  CellStack,
  DataTable,
  EvidencePanel,
  Metric,
  PageHeader,
  ReviewActionRow,
  ScoreBarCell,
  ScoreBarList,
  StackList,
  StatusPill,
  clamp,
} from "../components/ui.jsx";

export function CommandOverview({
  rankedScenarios,
  selectedScenario,
  setSelectedScenarioId,
  recordApproval,
  claims,
  readinessCategories,
}) {
  const topScenario = rankedScenarios[0];
  const decisionByScenarioId = new Map(
    generatedScenarioDecisionLedger.decisions.map((decision) => [decision.scenarioId, decision]),
  );
  const topScenarioDecision = decisionByScenarioId.get(topScenario.id) ?? generatedScenarioDecisionLedger.topDecision;
  const readinessScores = generatedReleaseEvidenceLedger.readinessScores;
  const topClosureBlockers = generatedBetaClosureStatus.blockerRows.slice(0, 3);
  const priorityEvidenceGaps = generatedEvidenceGapPriorityLedger.topGaps.slice(0, 3);
  const blockedReleaseGates = generatedReleaseEvidenceLedger.gates
    .filter((gate) => gate.status !== "pass")
    .slice(0, 5);

  return (
    <section className="module">
      <PageHeader
        title="COMMAND OVERVIEW"
        muted="Decision command"
        subtitle="Live ranked utility score with uncertainty and confidence gates."
      />
      <div className="module-grid">
        <article className="panel panel-large">
          <div className="panel-topline">
            <h2>Top recommendation</h2>
            <StatusPill tone="green">Weighted utility {topScenario?.scores.utility}%</StatusPill>
          </div>
          <h3>{topScenario.name}</h3>
          <p>{topScenario.summary}</p>
          <div className="metric-grid-4">
            <Metric label="Strategic fit" value={Math.round(topScenario.scores.benefitScore)} />
            <Metric label="Feasibility" value={Math.round(100 - topScenario.scores.riskScore)} />
            <Metric label="Decision readiness" value={round(topScenarioDecision.decisionReadinessScore, 1)} />
            <Metric label="Decision lane" value={topScenarioDecision.decisionLane.replace("lane-", "L")} />
            <Metric label="Blocked assumptions" value={topScenarioDecision.blockedAssumptionCount} />
            <Metric label="Decision hash" value={topScenarioDecision.decisionHash.slice(0, 18)} />
          </div>
          <ScoreBarList
            rows={[
              { label: "Strategic fit", value: topScenario.scores.benefitScore, tone: "blue" },
              { label: "Feasibility", value: 100 - topScenario.scores.riskScore, tone: "green" },
              { label: "Resource advantage", value: selectedScenarioResourceScore(topScenario), tone: "amber" },
              { label: "Risk profile", value: clamp(100 - topScenario.scores.riskScore, 0, 100), tone: "red" },
            ]}
          />
          <ReviewActionRow
            target={topScenario.name}
            recordApproval={recordApproval}
            actions={[
              {
                label: "Approve Recommendation",
                action: "approved",
                variant: "primary",
              },
              { label: "Defer Decision", action: "deferred" },
              { label: "Request More Research", action: "more-research" },
              { label: "Reject Recommendation", action: "rejected" },
            ]}
          />
        </article>

        <article className="panel">
          <div className="panel-topline">
            <h2>Approval state</h2>
            <StatusPill tone={generatedBetaClosureStatus.currentUseState.publicBetaAllowed ? "green" : "red"}>
              {generatedBetaClosureStatus.currentUseState.publicBetaAllowed ? "public beta allowed" : "fail-closed"}
            </StatusPill>
          </div>
          <p>
            {generatedBetaClosureStatus.currentUseState.reason}
          </p>
          <div className="metric-grid-1">
            <Metric label="Open claims" value={claims.filter((claim) => claim.status !== "Verified").length} />
            <Metric label="Formula checks" value={`${readinessCategories.length} tests`} />
            <Metric label="Release blockers" value={generatedReleaseEvidenceLedger.blockedGateCount} />
            <Metric label="Customer use" value={generatedBetaClosureStatus.currentUseState.customerDecisionUseAllowed ? "allowed" : "blocked"} />
          </div>
        </article>
      </div>

      <div className="panel panel-large">
        <div className="panel-topline">
          <h2>Scenario ranking</h2>
          <StatusPill tone="blue">{generatedScenarioDecisionLedger.mode}</StatusPill>
        </div>
        <p>{generatedScenarioDecisionLedger.operatingRule}</p>
        <DataTable
          className="rank-table"
          columns={[
            { key: "rank", header: "Rank", render: (_scenario, idx) => idx + 1 },
            {
              key: "scenario",
              header: "Scenario",
              render: (scenario) => <CellStack title={scenario.name} meta={scenario.summary} />,
            },
            {
              key: "confidence",
              header: "Decision lane",
              render: (scenario) => {
                const decision = decisionByScenarioId.get(scenario.id);
                return decision ? (
                  <CellStack
                    title={decision.decisionLane.replace("lane-", "L")}
                    meta={decision.promotionState}
                  />
                ) : (
                  "missing decision"
                );
              },
            },
            {
              key: "readiness",
              header: "Readiness",
              render: (scenario) => (
                <ScoreBarCell
                  value={decisionByScenarioId.get(scenario.id)?.decisionReadinessScore ?? 0}
                  tone="blue"
                  label={round(decisionByScenarioId.get(scenario.id)?.decisionReadinessScore ?? 0, 1)}
                />
              ),
            },
            {
              key: "strategicFit",
              header: "Strategic fit",
              render: (scenario) => (
                <ScoreBarCell value={scenario.scores.benefitScore} tone="blue" label={round(scenario.scores.benefitScore, 1)} />
              ),
            },
            {
              key: "feasibility",
              header: "Feasibility",
              render: (scenario) => (
                <ScoreBarCell value={100 - scenario.scores.riskScore} tone="green" label={round(100 - scenario.scores.riskScore, 1)} />
              ),
            },
            {
              key: "resource",
              header: "Resource",
              render: (scenario) => (
                <ScoreBarCell value={selectedScenarioResourceScore(scenario)} tone="amber" label={selectedScenarioResourceScore(scenario)} />
              ),
            },
            {
              key: "risk",
              header: "Risk",
              render: (scenario) => (
                <ScoreBarCell value={scenario.scores.riskScore} tone="red" label={round(scenario.scores.riskScore, 1)} />
              ),
            },
            {
              key: "blockers",
              header: "Proof blockers",
              render: (scenario) => decisionByScenarioId.get(scenario.id)?.blockers.length ?? "missing",
            },
            {
              key: "decisionHash",
              header: "Decision hash",
              render: (scenario) => {
                const decisionHash = decisionByScenarioId.get(scenario.id)?.decisionHash;
                return decisionHash ? <code>{decisionHash.slice(0, 18)}</code> : "missing";
              },
            },
            { key: "utility", header: "Utility", render: (scenario) => scenario.scores.utility },
          ]}
          rows={rankedScenarios}
          getRowKey={(scenario) => scenario.id}
          getRowClassName={(scenario) => (selectedScenario.id === scenario.id ? "is-active" : "")}
          onRowClick={(scenario) => setSelectedScenarioId(scenario.id)}
        />
      </div>

      <EvidencePanel
        title="Beta closure boundary"
        status={generatedBetaClosureStatus.mode}
        tone={generatedReleaseEvidenceLedger.releaseAllowed ? "green" : "red"}
        className="panel-large"
      >
        <p>{generatedBetaClosureStatus.operatingRule}</p>
        <div className="metric-grid-4">
          <Metric label="Release allowed" value={generatedReleaseEvidenceLedger.releaseAllowed ? "yes" : "no"} />
          <Metric label="Blocked release gates" value={generatedReleaseEvidenceLedger.blockedGateCount} />
          <Metric label="Closure blockers" value={generatedBetaClosureStatus.blockerCount} />
          <Metric label="Lowest readiness" value={generatedBetaClosureStatus.readinessSummary.lowestCurrentScore} />
          <Metric label="P0 evidence gaps" value={generatedEvidenceGapPriorityLedger.p0GapCount} />
          <Metric label="Experiment backlog" value={generatedEvidenceGapPriorityLedger.experimentBacklogCount} />
          <Metric label="Release hash" value={generatedReleaseEvidenceLedger.evidenceHashes.betaReviewDocket.slice(0, 18)} />
          <Metric label="Gap hash" value={generatedEvidenceGapPriorityLedger.evidenceHashes.scenarioTraceabilityMatrix.slice(0, 18)} />
        </div>
        <DataTable
          compact
          rows={blockedReleaseGates}
          getRowKey={(gate) => gate.id}
          columns={[
            {
              key: "gate",
              header: "Blocked gate",
              render: (gate) => <CellStack title={gate.label} meta={gate.owner} />,
            },
            {
              key: "status",
              header: "Status",
              render: (gate) => <StatusPill tone={gate.status === "blocked" ? "red" : "amber"}>{gate.status}</StatusPill>,
            },
            { key: "nextAction", header: "Next action" },
            { key: "requiredProof", header: "Required proof" },
          ]}
        />
      </EvidencePanel>

      <div className="triple-grid">
        <article className="panel">
          <div className="panel-topline">
            <h2>Top closure blockers</h2>
            <StatusPill tone="red">Critical</StatusPill>
          </div>
          <StackList
            items={topClosureBlockers}
            getTitle={(row) => row.label}
            getMeta={(row) => `${row.owner} / ${row.status}`}
            getBody={(row) => row.nextActions[0] ?? row.requiredProof}
          />
        </article>
        <article className="panel">
          <div className="panel-topline">
            <h2>P0 research actions</h2>
            <StatusPill tone="blue">Priority</StatusPill>
          </div>
          <StackList
            items={priorityEvidenceGaps}
            getTitle={(gap) => gap.title}
            getMeta={(gap) => `${gap.priorityLane} / ${gap.priorityScore}`}
            getBody={(gap) => gap.nextAction}
          />
        </article>
        <article className="panel">
          <div className="panel-topline">
            <h2>Readiness bars</h2>
            <StatusPill tone="amber">{generatedReleaseEvidenceLedger.readinessBlockingCount} blocking</StatusPill>
          </div>
          <ScoreBarList
            rows={readinessScores.map((score) => ({
              label: score.label,
              value: score.currentScore,
              valueLabel: `${score.currentScore}/${score.targetScore}`,
              tone: score.gap <= 2 ? "blue" : "amber",
            }))}
          />
          <p>{generatedReleaseEvidenceLedger.operatingRule}</p>
        </article>
      </div>
    </section>
  );
}

function selectedScenarioResourceScore(scenario) {
  return round(100 - scenario.uncertaintyPenalty * 3, 0);
}
